// ** React Imports
import {ReactNode, useContext, useEffect, useMemo} from 'react'
import * as yup from 'yup';

// ** Next Imports
import {Link, useLocation, useNavigate} from 'react-router-dom'

// ** MUI Components
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import MuiCard, { CardProps } from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import MuiFormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel'

// ** Icon Imports


// ** Layout Import
import BlankLayout from 'src/@core/layouts/BlankLayout'

import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import {yupResolver} from "@hookform/resolvers/yup";
import {useForm} from "react-hook-form";
import {AuthorizationRequest, AuthorizationResponse} from "../../types/user/authData";
import {HttpAuth} from "../../http/user/httpAuth";
import {userStorage} from "../../utils/localStorage/userStorage";
import {ControlledTextField} from "../../components/forms";
import {ControlledPasswordField} from "../../components/forms/ControlledPasswordField";
import {HttpAxiosRequest} from "../../http/httpAxiosBase";
import useAxios from "../../hooks/useAxios";
import {CompanyContext, CompanyContextValue} from "../solicitation-new/context/companyContext";

// ** Styled Components
const Card = styled(MuiCard)<CardProps>(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: '28rem' }
}))

const LinkStyled = styled(Link)(({ theme }) => ({
  fontSize: '0.875rem',
  textDecoration: 'none',
  color: theme.palette.primary.main
}))

const FormControlLabel = styled(MuiFormControlLabel)<FormControlLabelProps>(({ theme }) => ({
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  }
}))

enum LogInFormFields {
  Mail = 'vchMail',
  Password = 'vchPassword'
}

type LogInFormData = {
  [LogInFormFields.Mail]: string,
  [LogInFormFields.Password]: string
}

const LoginPage = () => {
  const navigate = useNavigate();
  const {fetchData} = useAxios();

  const {search} = useLocation();
  const {setClientId} = useContext<CompanyContextValue>(CompanyContext)
  const params = useMemo(() => new URLSearchParams(search), [search])

  const logInFormSchema = yup.object().shape({
    [LogInFormFields.Mail]: yup.string().email('Mail inválido').required('Campo obligatorio'),
    [LogInFormFields.Password]: yup.mixed().required('Campo obligatorio')
  })
  const {
    control,
    handleSubmit
  } = useForm<LogInFormData>({
    resolver: yupResolver(logInFormSchema),
  });

  function onLogIn(data: LogInFormData) {
    const request: AuthorizationRequest = {
      mail: data.vchMail,
      password: data.vchPassword,
      ipClient: '',
      codModulo: 17
    };
    
    fetchData(
        () => HttpAuth.authenticateUser(request),
        true
    )
        .then((response: AuthorizationResponse) => {
          HttpAxiosRequest.refreshToken(response.tokenAutorizacion.token);
            
          userStorage.save(
                response.idUsuario,
                response.idGrupo,
                response.idPerfil,
                response.idRolPersona,
                response.cantDiasParaCambiarPass,
                response.apellidoNombre,
                response.idiomaDefault,
                response.esGrupoBanco,
                response.esGrupoInterno,
                response.tokenAutorizacion
            );
                  
            navigate('/solicitudes');
            setClientId(0)
        })
  }


  useEffect(() => {
    HttpAuth.ping().then();
    if (params.get('fromLogout') === 'true') {
      userStorage.removeFromStorage();
    }

  }, [])

  return (
    <Grid container justifyContent={'center'} alignItems={'center'} sx={{ height: '100vh !important' }}>
      <Card sx={{ zIndex: 1 }}>
        <CardContent sx={{ p: theme => `${theme.spacing(12, 9, 7)} !important` }}>
          <Box sx={{ mb: 6 }}>
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant='h5' sx={{ fontWeight: 600, mb: 1.5 }}>
                Bienvenido ! 👋🏻
              </Typography>
              <img
                src={`/images/fogaba-resized.png`}
                height={'60'}
                alt={`logo-img`}
              />
            </Box>
            <Typography variant='body2'>Por favor, ingrese sus datos para acceder al sistema.</Typography>
          </Box>
          <form noValidate autoComplete='off' onSubmit={handleSubmit(onLogIn)}>
            <ControlledTextField control={control} name={LogInFormFields.Mail} autoFocus fullWidth id='email' label='Email' sx={{ mb: 4 }} />
            <ControlledPasswordField control={control} name={LogInFormFields.Password} label={'Contraseña'} fullWidth/>
            <Box
              sx={{ mb: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}
            >
              <FormControlLabel control={<Checkbox />} label='Recordar mi usuario' />
              <LinkStyled to='/forgot-password'>¿Olvidó su contraseña? Click aquí para recuperarla</LinkStyled>
            </Box>
            <Button fullWidth size='large' type='submit' variant='contained' sx={{ mb: 7 }}>
              Ingresar
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Typography variant='body2' sx={{ mr: 2 }}>
                ¿Eres nuevo en la plataforma?
              </Typography>
              <Typography variant='body2'>
                <LinkStyled to='/register'>Crear cuenta</LinkStyled>
              </Typography>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Grid>
  )
}

LoginPage.getLayout = (page: ReactNode) => <BlankLayout>{page}</BlankLayout>

LoginPage.guestGuard = true

export default LoginPage
